"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import { useOnNotification } from "@/shared/providers/notification-connection";

/**
 * Матч пользователя, который идёт прямо сейчас.
 *
 * KAN-365: живой прогон показал, что игроку неоткуда узнать о своём матче. Он
 * заходит на платформу и не видит ни того, что матч начался, ни того, что от него
 * ждут загрузку реплея — эта информация была спрятана внутри страницы турнира,
 * куда ещё надо догадаться зайти.
 *
 * Эндпоинт уже существовал: им пользуется десктоп-приложение, чтобы понять, в
 * какую игру слать результат. Веб просто им не пользовался.
 *
 * KAN: разбор прод-логов вечера турнира — этот хук (он в шапке, т.е. живёт на
 * КАЖДОЙ странице для каждого залогиненного) дал 18.4к запросов за ~9 часов,
 * почти все — 204 (матча нет, опрос пустоты). Честно посчитано по backend, что
 * реально покрыто пушем, а что нет:
 *   - раунд №2+ (динамическое планирование следующего раунда) шлёт участникам
 *     ReceiveNotification с eventType=match_reminder ЗАРАНЕЕ (за RoundBufferMinutes
 *     до старта) — на это можно среагировать мгновенно, не дожидаясь интервала;
 *   - пик/бан комнаты (PreDraft) шлют капитанам eventType=predraft_ready тоже
 *     заранее — тот же сигнал «скоро матч»;
 *   - а вот старт турнира и переход матчей 1-го раунда в Live (TryGoLive в
 *     TournamentStatusService) не шлют НИЧЕГО ни в один хаб — это подтверждено
 *     грепом по бэкенду (SendToTournamentAsync там не вызывается вовсе). Для
 *     обычного игрока (не капитана) это единственный случай, где опрос —
 *     единственный источник правды.
 * Поэтому интервал не убран, а лишь ослаблен (120с → 180с, страховка на случай
 * пропущенного события/старта 1-го раунда), а пуш подключён как быстрый путь
 * для покрытых случаев — обычно матч найдётся сильно раньше следующего тика.
 */

export type ActiveMatch = {
  matchId: string;
  tournamentId: string;
  tournamentType: string;
  tournamentName: string;
  bestOf: number;
  status: string;
};

export const ACTIVE_MATCH_QUERY_KEY = ["me", "active-match"] as const;

// Типы уведомлений, которые на бэке реально сопровождают появление/приближение
// активного матча (см. NotificationEventTypes в GoodLuck.Core/DTOs/NotificationDto.cs).
// Именно эти два типа сейчас шлются участникам матча заранее.
export const ACTIVE_MATCH_SIGNAL_EVENT_TYPES = new Set([
  "match_reminder",
  "predraft_ready",
]);

/**
 * Страховочный интервал для «моих» ручек в шапке.
 *
 * KAN-935, решение владельца 03.09.2026: опрос не выключается совсем даже там,
 * где есть пуш — события уже терялись молча (KAN-919), и без страховки человек
 * смотрел бы на устаревшее состояние, не зная об этом. Но интервал держится
 * редким, а в скрытой вкладке не работает вовсе.
 */
export const ME_SAFETY_REFETCH_MS = 180_000;

function normalizeActiveMatch(value: unknown): ActiveMatch | null {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value !== "object") {
    throw new Error("Invalid active match response");
  }
  const match = value as Partial<ActiveMatch>;
  if (
    typeof match.matchId !== "string" ||
    !match.matchId.trim() ||
    typeof match.tournamentId !== "string" ||
    !match.tournamentId.trim()
  ) {
    throw new Error("Invalid active match response");
  }
  return {
    matchId: match.matchId,
    tournamentId: match.tournamentId,
    tournamentType:
      typeof match.tournamentType === "string" ? match.tournamentType : "",
    tournamentName:
      typeof match.tournamentName === "string" ? match.tournamentName : "",
    bestOf:
      typeof match.bestOf === "number" && Number.isFinite(match.bestOf)
        ? match.bestOf
        : 1,
    status: typeof match.status === "string" ? match.status : "",
  };
}

export function useActiveMatch(enabled: boolean) {
  const queryClient = useQueryClient();

  // Быстрый путь: как только приходит уведомление, которое на бэке означает
  // «у тебя скоро/уже матч», перечитываем active-match сразу, не дожидаясь
  // страховочного интервала. Хаб уведомлений и так всегда поднят для
  // авторизованного пользователя (NotificationConnectionProvider) — отдельный
  // сокет здесь не нужен.
  useOnNotification(
    useCallback(
      (notification) => {
        if (!enabled) return;
        if (!notification.eventType) return;
        if (!ACTIVE_MATCH_SIGNAL_EVENT_TYPES.has(notification.eventType)) return;
        void queryClient.invalidateQueries({ queryKey: ACTIVE_MATCH_QUERY_KEY });
      },
      [enabled, queryClient],
    ),
  );

  return useQuery<ActiveMatch | null>({
    queryKey: ACTIVE_MATCH_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<ActiveMatch | null>("/me/active-match");
      // KAN-1096: `|| null`, а не `?? null`. «Матча нет» приходит как 204 No
      // Content (так ASP.NET отдаёт `Ok(null)`), а axios на пустое тело кладёт в
      // `data` ПУСТУЮ СТРОКУ. С `??` в кэш React Query легла бы `""` — значение,
      // которого нет в типе. Шапка это переживала случайно (читает поля через
      // `?.`), но первый же `activeMatch.tournamentId` без `?.` упал бы, а тип
      // такое написать разрешает.
      return normalizeActiveMatch(res.data);
    },
    enabled,
    // Матч живёт десятками минут — чаще спрашивать незачем, а пункт меню всё
    // равно появится при следующей навигации.
    staleTime: 60_000,
    // Страховка, не основной источник: старт 1-го раунда пуша не имеет (см.
    // комментарий выше), поэтому совсем убрать интервал нельзя. 180с вместо
    // прежних 120с — компенсируем тем, что пуш теперь ловит бОльшую часть
    // случаев (раунд 2+, PreDraft) почти мгновенно.
    refetchInterval: ME_SAFETY_REFETCH_MS,
    // KAN-935: в свёрнутой вкладке интервал молчит. Забытая вкладка — самая
    // дорогая и самая бессмысленная часть нагрузки: данные обновляются для
    // никого. Возврат на вкладку компенсируется refetchOnWindowFocus ниже.
    refetchIntervalInBackground: false,
    // Глобальный дефолт проекта — refetchOnWindowFocus: false (shared/query-client.ts),
    // и включать его на весь проект нельзя. Но именно здесь он обязателен: без него
    // гейт по видимости чинил бы нагрузку ценой правильности — вернувшийся на вкладку
    // человек ждал бы до трёх минут, прежде чем увидит свой матч.
    refetchOnWindowFocus: true,
    // Молча: отсутствие активного матча — норма, а не ошибка, и шуметь тут нечем.
    retry: false,
  });
}
